// services/account.service.js
import httpClient from './httpClient';
import TokenService from './token.service';
import { extractDetailMessage } from '../utils/errorDetail';

/**
 * Account Service
 * Maneja todas las operaciones relacionadas con Accounts (Personal y Organization)
 * Base URL configured via REACT_APP_AUTH_API_URL or REACT_APP_API_BASE_URL
 */

// Usar cliente HTTP centralizado
const authClient = httpClient;

/**
 * Normalizes error payloads from modern HTTP-status-based responses.
 * Returns null when the body has no error text (e.g. conflict body is the resource itself).
 * @param {Error} error - Axios error object
 * @returns {string[] | null}
 */
const normalizeAccountError = (error) => {
  const errorData = error.response?.data;
  const raw = errorData?.error;
  if (Array.isArray(raw) && raw.length > 0) return raw;
  if (raw && typeof raw === 'string') return [raw];
  if (errorData?.message) return [errorData.message];
  if (errorData?.detail) return [extractDetailMessage(errorData.detail)];
  return null;
};

const AccountService = {
  /**
   * Obtener cuenta personal del usuario
   * Opción 1: Usar default_account_id de localStorage (guardado en login)
   * Opción 2: Filtrar lista de cuentas por tipo PERSONAL
   */
  async getPersonalAccount() {
    try {
      // Opción 1: Intentar obtener desde default_account_id guardado en login
      const defaultAccountId = localStorage.getItem('default_account_id');
      
      if (defaultAccountId) {
        try {
          const response = await authClient.get(`/auth/accounts/${defaultAccountId}`);
          const account = response.data?.body?.account ?? response.data?.body ?? response.data;
          if (account?.type === 'PERSONAL') {
            return { success: true, status: response.status, data: account };
          }
        } catch (err) {
          // Stale default_account_id (e.g. from a previous user session) — clear it
          // so we don't keep hitting a 403 on every load, then fall through to list.
          localStorage.removeItem('default_account_id');
          console.warn('Could not fetch account by default_account_id, trying list method');
        }
      }

      // Opción 2: Listar todas las cuentas y filtrar por PERSONAL
      const listResponse = await authClient.get('/auth/accounts');
      const listBody = listResponse.data?.body ?? listResponse.data;
      const accounts = listBody?.accounts ?? (Array.isArray(listBody) ? listBody : null);

      if (!Array.isArray(accounts)) {
        return { success: false, error: ['Invalid accounts response format'] };
      }
      
      const personalAccount = accounts.find(account => account.type === 'PERSONAL');
      
      if (personalAccount) {
        localStorage.setItem('default_account_id', personalAccount.id);
        return { success: true, status: listResponse.status, data: personalAccount };
      }
      
      return { success: false, error: ['No personal account found'] };
      
    } catch (error) {
      console.error('Error fetching personal account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to fetch personal account']
      };
    }
  },

  /**
   * Obtener cuenta de organización
   * GET /organizations/{organization_id}
   */
  async getOrganizationAccount(organizationId) {
    try {
      const response = await authClient.get(`/auth/organizations/${organizationId}`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body?.organization ?? response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error fetching organization account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to fetch organization account']
      };
    }
  },

  /**
   * Obtener detalles de una cuenta por ID
   * GET /accounts/{account_id}
   */
  async getAccount(accountId) {
    try {
      const response = await authClient.get(`/auth/accounts/${accountId}`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body?.account ?? response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error fetching account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to fetch account']
      };
    }
  },

  /**
   * Actualizar cuenta
   * PUT /accounts/{account_id}
   */
  async updateAccount(accountId, updates) {
    try {
      const response = await authClient.put(`/auth/accounts/${accountId}`, updates);
      return {
        success: true,
        status: response.status,
        data: response.data?.body?.account ?? response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error updating account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to update account']
      };
    }
  },

  /**
   * Listar todas las cuentas del usuario
   * GET /accounts?user_id=&organization_id=&type=&limit=20&offset=0
   * 
   * @param {Object} params - Parámetros de filtrado y paginación
   * @param {string} params.user_id - Filtrar por usuario (opcional)
   * @param {string} params.organization_id - Filtrar por organización (opcional)
   * @param {string} params.type - Filtrar por tipo PERSONAL/ORGANIZATION (opcional)
   * @param {number} params.limit - Registros por página (default: 20, max: 100)
   * @param {number} params.offset - Desplazamiento para paginación (default: 0)
   */
  async listAccounts({ user_id, organization_id, type, limit = 20, offset = 0 } = {}) {
    try {
      const params = {};
      if (user_id) params.user_id = user_id;
      if (organization_id) params.organization_id = organization_id;
      if (type) params.type = type;
      params.limit = limit;
      params.offset = offset;

      const response = await authClient.get('/auth/accounts', { params });
      const body = response.data?.body ?? response.data;
      const accounts = body?.accounts ?? (Array.isArray(body) ? body : []);
      return {
        success: true,
        status: response.status,
        data: accounts,
        totalCount: body?.total_count ?? accounts.length
      };
    } catch (error) {
      console.error('Error listing accounts:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to list accounts'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Crear cuenta
   * POST /accounts
   *
   * Reglas del backend (shared_core, CreateAccountRequest): una cuenta personal
   * exige `owner_id` y no lleva organización; una de organización exige
   * `organization_id` y el `owner_id` es opcional. Mandar los dos es un error,
   * así que aquí se manda exactamente uno.
   *
   * @param {Object} data
   * @param {string} data.name
   * @param {string} [data.description]
   * @param {string} [data.organizationId] - crea una cuenta de organización
   * @param {string} [data.ownerId]        - crea una cuenta personal
   */
  async createAccount({ name, description, organizationId, ownerId } = {}) {
    try {
      const payload = {
        name,
        type: organizationId ? 'ORGANIZATION' : 'PERSONAL',
      };
      if (description) payload.description = description;
      if (organizationId) payload.organization_id = organizationId;
      else if (ownerId) payload.owner_id = ownerId;

      const response = await authClient.post('/auth/accounts', payload);
      const body = response.data?.body ?? response.data;
      return {
        success: true,
        status: response.status,
        data: body?.account ?? body,
      };
    } catch (error) {
      console.error('Error creating account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to create account'],
      };
    }
  },

  /**
   * Suspender cuenta
   * POST /accounts/{account_id}/suspend
   */
  async suspendAccount(accountId) {
    try {
      const response = await authClient.post(`/auth/accounts/${accountId}/suspend`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error suspending account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to suspend account']
      };
    }
  },

  /**
   * Reactivar cuenta
   * POST /accounts/{account_id}/reactivate
   */
  async reactivateAccount(accountId) {
    try {
      const response = await authClient.post(`/auth/accounts/${accountId}/reactivate`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error reactivating account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to reactivate account']
      };
    }
  },

  /**
   * Eliminar cuenta
   * DELETE /accounts/{account_id}
   */
  async deleteAccount(accountId) {
    try {
      const response = await authClient.delete(`/auth/accounts/${accountId}`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error deleting account:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to delete account']
      };
    }
  },

  /**
   * Obtener información del tier de una cuenta
   * GET /accounts/{account_id}/tier
   */
  async getAccountTier(accountId) {
    try {
      const response = await authClient.get(`/auth/accounts/${accountId}/tier`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body?.tier ?? response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error fetching account tier:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to fetch account tier']
      };
    }
  },

  /**
   * Obtener información de un tier por ID
   * GET /tiers/{tier_id}
   */
  async getTier(tierId) {
    try {
      const response = await authClient.get(`/auth/tiers/${tierId}`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body?.tier ?? response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error fetching tier:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to fetch tier']
      };
    }
  },

  /**
   * Listar todos los tiers disponibles
   * GET /tiers
   */
  async listTiers() {
    try {
      const response = await authClient.get('/auth/tiers');
      const body = response.data?.body ?? response.data;
      const tiers = body?.tiers ?? (Array.isArray(body) ? body : []);
      return {
        success: true,
        status: response.status,
        data: tiers
      };
    } catch (error) {
      console.error('Error listing tiers:', error);
      return {
        success: false,
        status: error.response?.status,
        error: normalizeAccountError(error) ?? ['Failed to list tiers'],
        data: []
      };
    }
  }
};

export default AccountService;
